import React, { useEffect, useState } from 'react';
import { Check, TriangleAlert } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  CenteredH2,
  ModalFooter,
  ModalContent,
  ModalField,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  SuccessMessageHeadline,
  SuccessMessageSubHeadline,
  IconContainer,
  MessageContainer,
  ErrorMessageHeadline,
  ErrorMessageSubHeadline,
  ErrorIconContainer,
  SuccessIconContainer,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import FileUploadButton from '../../../components/Inputs/FileUploadComponent/FileUploadComponent';
import CustomCheckbox from '../../../components/Inputs/Checkbox/Checkbox';
import CloseButton from '../../../components/CloseButton/CloseButton';
import { cropConnectApiClient } from '../../../../services/cropConnectApiClient';

interface UploadBannerModalProps {
  isOpen: boolean;
  widgetId: string | undefined;
  onCloseAfterUpload: () => void;
  onCloseWithoutUpload: () => void;
  documentType: string;
}

const UploadBannerModal: React.FC<UploadBannerModalProps> = ({
  isOpen,
  widgetId,
  onCloseAfterUpload,
  onCloseWithoutUpload,
  documentType,
}) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isFileInvalid, setIsFileInvalid] = useState(false);
  const [isConfirmationInvalid, setIsConfirmationInvalid] = useState(false);

  const resetState = () => {
    setIsUploaded(false);
    setIsUploadSuccessful(false);
    setFile(null);
    setConfirmed(false);
    setIsFileInvalid(false);
    setIsConfirmationInvalid(false);
  };

  const handleFile = (file: File) => {
    setFile(file);
    setIsFileInvalid(false);
  };

  const handleUpload = async () => {
    let isValid = true;
    if (!file) {
      setIsFileInvalid(true);
      isValid = false;
    }
    if (!confirmed) {
      setIsConfirmationInvalid(true);
      isValid = false;
    }
    if (!isValid) {
      return;
    }
    setLoading(true); // Show loading indicator
    setIsUploaded(true);
    try {
      const uploadingDocument = await cropConnectApiClient.uploadDocument(
        documentType,
        widgetId,
        {
          data: file,
          fileName: file?.name || '',
        }
      );
      if (uploadingDocument.success) {
        setIsUploadSuccessful(true);
      } else {
        setIsUploadSuccessful(false);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setIsUploadSuccessful(false);
    }

    setLoading(false); // Hide loading indicator
  };

  const handleOnCancel = () => {
    if (isUploaded) {
      onCloseAfterUpload();
      resetState();
    } else {
      onCloseWithoutUpload();
    }
  };

  const handleStartAgain = () => {
    resetState();
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  let modalContent;

  if (loading) {
    modalContent = (
      <ModalOverlay>
        <ModalContent>
          <CenteredH2>{t(TranslationKeys.general_document_loading)}</CenteredH2>
        </ModalContent>
      </ModalOverlay>
    );
    return createPortal(modalContent, document.body);
  }

  modalContent = (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <CenteredH2>
            {t(TranslationKeys.upload_document_banner_title)}
          </CenteredH2>
          <CloseButton onClick={handleOnCancel} />
        </ModalHeader>

        <ModalBody>
          {isUploaded && isUploadSuccessful && (
            <MessageContainer>
              <IconContainer>
                <SuccessIconContainer>
                  <Check size={32} strokeWidth={3} color="#FFFFFF" />
                </SuccessIconContainer>
              </IconContainer>
              <SuccessMessageHeadline>
                {t(
                  TranslationKeys.upload_document_banner_success_message_headline
                )}
              </SuccessMessageHeadline>
              <div>
                <SuccessMessageSubHeadline>
                  {t(
                    TranslationKeys.upload_document_banner_success_message_subheadline_1
                  )}
                </SuccessMessageSubHeadline>
                <SuccessMessageSubHeadline>
                  {t(
                    TranslationKeys.upload_document_banner_success_message_subheadline_2
                  )}
                </SuccessMessageSubHeadline>
              </div>
            </MessageContainer>
          )}
          {isUploaded && !isUploadSuccessful && (
            <MessageContainer>
              <IconContainer>
                <ErrorIconContainer>
                  <TriangleAlert size={32} color="#FFFFFF" />
                </ErrorIconContainer>
              </IconContainer>
              <ErrorMessageHeadline>
                {t(
                  TranslationKeys.upload_document_banner_error_message_headline
                )}
              </ErrorMessageHeadline>
              <div>
                <ErrorMessageSubHeadline>
                  {t(
                    TranslationKeys.upload_document_banner_error_message_subheadline_1
                  )}
                </ErrorMessageSubHeadline>
                <ErrorMessageSubHeadline>
                  {t(
                    TranslationKeys.upload_document_banner_error_message_subheadline_2
                  )}
                </ErrorMessageSubHeadline>
              </div>
            </MessageContainer>
          )}
          {!isUploaded && (
            <>
              <ModalField>
                <h3>
                  {t(TranslationKeys.upload_document_banner_document_type)}
                </h3>
                <input type="text" value={documentType} disabled />
              </ModalField>

              <FileUploadButton
                onFileSelected={handleFile}
                isError={isFileInvalid}
              />
              <CustomCheckbox
                checked={confirmed}
                onChange={() => {
                  setConfirmed(!confirmed);
                  setIsConfirmationInvalid(false);
                }}
                label={t(
                  TranslationKeys.upload_document_banner_confirmation_checkbox_text
                )}
                labelFontSize="14px"
                isError={isConfirmationInvalid}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {isUploaded && isUploadSuccessful && (
            <ActionButton
              variant="accept-blue"
              buttonText={t(
                TranslationKeys.upload_document_banner_modal_cancel_button
              )}
              onClickAction={handleOnCancel}
            />
          )}
          {isUploaded && !isUploadSuccessful && (
            <>
              <ActionButton
                variant="close"
                buttonText={t(
                  TranslationKeys.upload_document_banner_modal_cancel_button
                )}
                onClickAction={handleOnCancel}
              />
              <ActionButton
                variant="accept-blue"
                buttonText={t(
                  TranslationKeys.upload_document_banner_modal_start_again_button
                )}
                onClickAction={handleStartAgain}
              />
            </>
          )}
          {!isUploaded && (
            <>
              <ActionButton
                variant="close"
                buttonText={t(
                  TranslationKeys.upload_document_banner_modal_cancel_button
                )}
                onClickAction={handleOnCancel}
              />
              <ActionButton
                variant="accept-blue"
                buttonText={t(
                  TranslationKeys.upload_document_banner_modal_upload_button
                )}
                onClickAction={handleUpload}
              />
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
  return createPortal(modalContent, document.body);
};

export default UploadBannerModal;
